import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentMethodModal = ({ 
  isOpen = false,
  onClose = () => {},
  participant = null,
  onPaymentComplete = () => {}
}) => {
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  const paymentMethods = [
    { id: 'card', name: 'Tarjeta de Crédito/Débito', icon: 'CreditCard' },
    { id: 'paypal', name: 'PayPal', icon: 'Wallet' },
    { id: 'bizum', name: 'Bizum', icon: 'Smartphone' },
    { id: 'cash', name: 'Efectivo', icon: 'Banknote' }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handleCardInputChange = (field, value) => {
    setCardDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      onPaymentComplete(participant?.id, selectedMethod);
      onClose();
    }, 2000);
  };

  const handleCashPayment = () => {
    onPaymentComplete(participant?.id, 'cash');
    onClose();
  };

  if (!isOpen || !participant) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-surface border border-border rounded-lg shadow-elevation-3 w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">
              Método de Pago
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          <div className="p-4">
            {/* Payment Summary */}
            <div className="mb-6 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                  {participant?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
                </div>
                <span className="font-medium text-foreground">{participant?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Total a pagar:</span>
                <span className="font-mono font-semibold text-lg text-foreground">
                  {formatAmount(participant?.totalAmount)}
                </span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="mb-6">
              <label className="text-sm font-medium text-foreground mb-3 block">
                Selecciona método de pago:
              </label>
              <div className="space-y-2">
                {paymentMethods?.map((method) => (
                  <button
                    key={method?.id}
                    onClick={() => setSelectedMethod(method?.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                      selectedMethod === method?.id
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border bg-card hover:border-primary/30 text-foreground'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name={method?.icon} size={20} />
                      <span className="font-medium">{method?.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Form */}
            {selectedMethod === 'card' && (
              <div className="space-y-4 mb-6">
                <Input
                  label="Número de tarjeta"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  value={cardDetails?.number}
                  onChange={(e) => handleCardInputChange('number', e?.target?.value)}
                  maxLength="19"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Fecha de vencimiento"
                    type="text"
                    placeholder="MM/AA"
                    value={cardDetails?.expiry}
                    onChange={(e) => handleCardInputChange('expiry', e?.target?.value)}
                    maxLength="5"
                  />
                  <Input
                    label="CVV"
                    type="text"
                    placeholder="123"
                    value={cardDetails?.cvv}
                    onChange={(e) => handleCardInputChange('cvv', e?.target?.value)}
                    maxLength="4"
                  />
                </div>

                <Input
                  label="Nombre del titular"
                  type="text"
                  placeholder="Nombre como aparece en la tarjeta"
                  value={cardDetails?.name}
                  onChange={(e) => handleCardInputChange('name', e?.target?.value)}
                />
              </div>
            )}

            {selectedMethod === 'bizum' && (
              <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="text-center">
                  <Icon name="Smartphone" size={32} className="mx-auto mb-2 text-primary" />
                  <p className="text-sm text-foreground mb-2">
                    Abre tu app de Bizum y envía:
                  </p>
                  <p className="font-mono font-semibold text-lg text-foreground">
                    {formatAmount(participant?.totalAmount)}
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    Al número: 612 345 678
                  </p>
                </div>
              </div>
            )}

            {selectedMethod === 'paypal' && (
              <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="text-center">
                  <Icon name="Wallet" size={32} className="mx-auto mb-2 text-primary" />
                  <p className="text-sm text-foreground">
                    Serás redirigido a PayPal para completar el pago
                  </p>
                </div>
              </div>
            )}

            {selectedMethod === 'cash' && (
              <div className="mb-6 p-4 bg-warning/5 border border-warning/20 rounded-lg">
                <div className="text-center">
                  <Icon name="Banknote" size={32} className="mx-auto mb-2 text-warning" />
                  <p className="text-sm text-foreground">
                    Pago en efectivo - Confirma cuando hayas pagado al camarero
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              {selectedMethod === 'cash' ? (
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleCashPayment}
                  iconName="Check"
                  iconPosition="left"
                  iconSize={16}
                >
                  Confirmar Pago en Efectivo
                </Button>
              ) : (
                <Button
                  variant="default"
                  fullWidth
                  onClick={handlePayment}
                  loading={isProcessing}
                  iconName="CreditCard"
                  iconPosition="left"
                  iconSize={16}
                >
                  {isProcessing ? 'Procesando...' : `Pagar ${formatAmount(participant?.totalAmount)}`}
                </Button>
              )}
              
              <Button
                variant="outline"
                fullWidth
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentMethodModal;